import { CreateRounded } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { enableDisableMenu, verifyUser } from '../../../../../../../redux/actions/users'
import { IRootState } from '../../../../../../../redux/types'
import ModalInfo from '../../../../../../shared/components/modals/ModalInfo'
import SubtitleGrey from '../../../../../../shared/styles/SubtitleGrey'
import { isAdmin, isClient, isCommercial, isManager, isNotClientAndTrainer } from '../../../../utils/RolesHelper'
import { MenusUserInfo } from './MenusUserInfo'
import { VerificationUserInfo } from './VerificationUserInfo'

interface DesktopUserInfoProps {
  openChangePassword: boolean
  setOpenChangePassword: (value: boolean) => void
  user_id: number | null
}

export function DesktopUserInfo({ openChangePassword, setOpenChangePassword, user_id }: DesktopUserInfoProps) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { userProfileSelected } = useSelector((state: IRootState) => state.user)

  const [verification, setVerification] = useState(userProfileSelected.verification)

  const handleCheckChange = (event: any) => {
    const verificationId: number = Number.parseInt(event.target.value)
    setVerification(verificationId)
    dispatch(verifyUser({ user_id, verificationId }))
  }

  const handleClickAccessMenu = (menu_id: number) => (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(
      enableDisableMenu({
        user_id: userProfileSelected.id,
        menu_id,
        status: event.target.checked
      })
    )
  }

  useEffect(() => {
    if (userProfileSelected !== null) setVerification(userProfileSelected.verification)
  }, [userProfileSelected])

  return (
    <Grid container justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Grid xs={12} md={6} lg={4}>
        <Box pb={3}>
          <Typography color="textSecondary" variant="subtitle1">
            {t('personal-info').toUpperCase()}
          </Typography>
        </Box>
        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('email')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.email}</Typography>
        </Box>
        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('phone')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.phone}</Typography>
        </Box>
        {isCommercial(userProfileSelected.role_id) && (
          <Box pb={1}>
            <SubtitleGrey variant="subtitle1">{t('admin.people.code')}</SubtitleGrey>
            <Typography variant="body1">{userProfileSelected.commercial_code}</Typography>
          </Box>
        )}
        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('language')}</SubtitleGrey>
          <Typography variant="body1">
            {userProfileSelected.languages
              .map((language: any) => {
                return language.name
              })
              .join(' - ')}
          </Typography>
        </Box>
        {isNotClientAndTrainer(authUser.role_id) && authUser.role.code < userProfileSelected.roleCode && (
          <Button
            id="btn-change-password"
            fullWidth={true}
            onClick={() => {
              setOpenChangePassword(!openChangePassword)
            }}
            variant="contained"
            color="primary"
            startIcon={<CreateRounded />}
          >
            {t('login.change-password')}
          </Button>
        )}
      </Grid>
      <Grid xs={12} md={6} lg={4}>
        <Box pb={3}>
          <Typography color="textSecondary" variant="subtitle1">
            {isClient(userProfileSelected.role_id) ? t('admin.people.center-data').toUpperCase() : t('address-data').toUpperCase()}
          </Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('country')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.countryName}</Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('province')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.provinceName}</Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('town')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.townName}</Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('address')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.address}</Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('postal-code')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.postalcode}</Typography>
        </Box>
      </Grid>
      <Grid xs={12} md={6} lg={4}>
        <Box pb={3}>
          <Typography color="textSecondary" variant="subtitle1">
            {t('admin.people.accesses-and-records').toUpperCase()}
          </Typography>
        </Box>
        {isManager(authUser.role_id) && <MenusUserInfo handleClickAccessMenu={handleClickAccessMenu} />}
        {(isAdmin(authUser.role_id) || authUser.can_verify === 1) && (
          <VerificationUserInfo verification={verification} handleCheckChange={handleCheckChange} />
        )}
        <Box pb={1}>
          <ModalInfo title={t('admin.people.manage-countries')} options={userProfileSelected.countries} />
        </Box>
        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('admin.people.created-date')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.created_date}</Typography>
        </Box>

        <Box pb={1}>
          <SubtitleGrey variant="subtitle1">{t('admin.people.rec-from')}</SubtitleGrey>
          <Typography variant="body1">{userProfileSelected.rec_from === null ? 'unknown' : userProfileSelected.rec_from}</Typography>
        </Box>
      </Grid>
    </Grid>
  )
}
